import { PAYOUT_DECIMALS, type PnlRewardSlotTickets } from "../trade-to-earn/trade-to-earn-rules";
import Decimal from "decimal.js";

/**
 * Arena prize split: how a pot is paid by finishing place. Slots are weights, not amounts:
 * `{quantity: 5, reward: "1"}` is "top 5 equally", `[{1, "50"}, {2, "25"}]` is 50 / 25 / 25.
 * The pot is only known at start, so the API, Arena and Solo CMS all size it through
 * {@link distributePrizePot}. A free competition's slots are literal amounts instead
 * ({@link distributeFixedFreePrizes}). Ticket prizes are Cede-funded, off the pot: they ride
 * along with their place untouched.
 */

const FULL_POT_PERCENT = new Decimal(100);
const SHARE_DECIMALS = 2;

export interface PrizeSplitWeight {
  quantity: number;
  reward: Decimal.Value;
  tickets?: PnlRewardSlotTickets;
}

export interface PrizeSplitPaidSlot {
  quantity: number;
  reward: Decimal;
  tickets?: PnlRewardSlotTickets;
}

export interface PrizeSplitDistribution {
  /** The pot actually paid: the input rounded down to cents. Σ slots equals it exactly. */
  pot: Decimal;
  /** Absolute amounts, highest first. */
  slots: PrizeSplitPaidSlot[];
  /** Cents left after rounding down, already added to 1st. */
  roundingToFirst: Decimal;
  /** Paid places with no player; their share is re-spread over the filled ones. */
  unfilledPlaces: number;
}

export const paidPlaceCount = (slots: ReadonlyArray<{ quantity: number }>): number =>
  slots.reduce((sum, slot) => sum + slot.quantity, 0);

/** Keeps `tickets` absent rather than `undefined`, so a ticket-less slot serializes as before. */
const withTickets = <T extends object>(
  slot: T,
  tickets: PnlRewardSlotTickets | undefined,
): T & { tickets?: PnlRewardSlotTickets } => (tickets == null ? slot : { ...slot, tickets });

type PlacedSlot = { quantity: number; weight: Decimal; tickets?: PnlRewardSlotTickets };

/** Keeps the `playerCount` highest places, by reward then array order (stable), as ranking assigns them. */
function fillPlaces(
  weights: readonly PrizeSplitWeight[],
  playerCount: number,
): { filled: PlacedSlot[]; unfilledPlaces: number } {
  const byDescendingWeight = weights
    .map((slot) => withTickets({ quantity: slot.quantity, weight: new Decimal(slot.reward) }, slot.tickets))
    .sort((a, b) => b.weight.cmp(a.weight));
  let remainingPlaces = Math.max(0, playerCount);
  const filled: PlacedSlot[] = [];
  for (const slot of byDescendingWeight) {
    if (remainingPlaces <= 0) break;
    const quantity = Math.min(slot.quantity, remainingPlaces);
    filled.push({ ...slot, quantity });
    remainingPlaces -= quantity;
  }
  return { filled, unfilledPlaces: paidPlaceCount(weights) - paidPlaceCount(filled) };
}

/** The split cannot pay its pot: a positive pot with no weighted place, or weights too large to scale. */
export class PrizePotUnpayableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PrizePotUnpayableError";
  }
}

const parseDecimal = (value: Decimal.Value): Decimal | null => {
  try {
    const parsed = new Decimal(value);
    return parsed.isFinite() ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Pays `pot` by place: the highest `playerCount` places are kept, the pot is scaled across their
 * USDT weights, each amount rounds down to cents and the leftover cents go to 1st. A place's
 * `tickets` pass through unchanged (a ticket-only place is weighted 0). No weights means winner
 * takes all. Throws {@link PrizePotUnpayableError} when a positive pot has no weighted place to pay
 * (no players, or only zero weights) or a payout is not finite; callers validate the weights themselves.
 */
export function distributePrizePot(params: {
  weights: readonly PrizeSplitWeight[];
  pot: Decimal;
  playerCount: number;
}): PrizeSplitDistribution {
  const pot = params.pot.toDecimalPlaces(PAYOUT_DECIMALS, Decimal.ROUND_DOWN);
  const weights = params.weights.length > 0 ? params.weights : [{ quantity: 1, reward: 1 }];
  const { filled, unfilledPlaces } = fillPlaces(weights, params.playerCount);

  const totalWeight = filled.reduce((sum, slot) => sum.add(slot.weight.mul(slot.quantity)), new Decimal(0));
  if (totalWeight.isZero()) {
    if (pot.gt(0)) throw new PrizePotUnpayableError("A positive prize pot has no weighted place to pay");
    return {
      pot,
      slots: filled.map((slot) => withTickets({ quantity: slot.quantity, reward: new Decimal(0) }, slot.tickets)),
      roundingToFirst: new Decimal(0),
      unfilledPlaces,
    };
  }

  const scaled = filled.map((slot) =>
    withTickets(
      {
        quantity: slot.quantity,
        reward: slot.weight.mul(pot).div(totalWeight).toDecimalPlaces(PAYOUT_DECIMALS, Decimal.ROUND_DOWN),
      },
      slot.tickets,
    ),
  );
  if (!totalWeight.isFinite() || scaled.some((slot) => !slot.reward.isFinite())) {
    throw new PrizePotUnpayableError("The prize split's weights are too large to scale to the pot");
  }
  const paid = scaled.reduce((sum, slot) => sum.add(slot.reward.mul(slot.quantity)), new Decimal(0));
  const roundingToFirst = pot.sub(paid);
  const [first, ...rest] = scaled;
  if (first == null || roundingToFirst.isZero()) return { pot, slots: scaled, roundingToFirst, unfilledPlaces };

  const firstPlace = { ...first, quantity: 1, reward: first.reward.add(roundingToFirst) };
  const sharedWithFirst = first.quantity > 1 ? [{ ...first, quantity: first.quantity - 1 }] : [];
  return { pot, slots: [firstPlace, ...sharedWithFirst, ...rest], roundingToFirst, unfilledPlaces };
}

/**
 * A free competition's payout: slots are literal USDT amounts, Cede-funded. The `playerCount`
 * highest places are paid as configured; unfilled places are dropped, not re-spread, so `pot`
 * (Σ paid) can be below the configured prize pool.
 */
export function distributeFixedFreePrizes(params: {
  slots: readonly PrizeSplitWeight[];
  playerCount: number;
}): PrizeSplitDistribution {
  const { filled, unfilledPlaces } = fillPlaces(params.slots, params.playerCount);
  const slots = filled.map((slot) => withTickets({ quantity: slot.quantity, reward: slot.weight }, slot.tickets));
  const pot = slots.reduce((sum, slot) => sum.add(slot.reward.mul(slot.quantity)), new Decimal(0));
  return { pot, slots, roundingToFirst: new Decimal(0), unfilledPlaces };
}

// ── The editor's model, shared by the Arena room forms and the Solo CMS blueprint ──

export interface PrizeSplitRow {
  places: number;
  /** Share of the pot each of these places takes, as typed ("12.5"). */
  percentEach: string;
}

export type PrizeSplitMode = "winner" | "equal" | "custom";

/** One value per mode, so switching modes never wipes one; only `mode`'s value is the split. */
export interface PrizeSplitDraft {
  mode: PrizeSplitMode;
  equalPlaces: number;
  customRows: PrizeSplitRow[];
}

export const PRIZE_SPLIT_DEFAULT_EQUAL_PLACES = 3;
export const PRIZE_SPLIT_DEFAULT_CUSTOM_ROWS: readonly PrizeSplitRow[] = [
  { places: 1, percentEach: "50" },
  { places: 2, percentEach: "25" },
];

export const defaultPrizeSplitDraft = (mode: PrizeSplitMode = "winner"): PrizeSplitDraft => ({
  mode,
  equalPlaces: PRIZE_SPLIT_DEFAULT_EQUAL_PLACES,
  customRows: PRIZE_SPLIT_DEFAULT_CUSTOM_ROWS.map((row) => ({ ...row })),
});

export const PRIZE_SPLIT_PLACE_COUNT_MESSAGE = "Enter 1 or more paid places.";

export const isPlaceCount = (places: number): boolean => Number.isInteger(places) && places >= 1;

/** A count as typed: digits only, from 1 ("007" is 7). Anything else is NaN, which fails `isPrizeSplitValid`. */
export function parsePlaceCount(text: string): number {
  const places = /^\d+$/.test(text) ? Number(text) : Number.NaN;
  return places >= 1 ? places : Number.NaN;
}

export function ordinal(place: number): string {
  const teens = place % 100;
  if (teens >= 11 && teens <= 13) return `${place}th`;
  switch (place % 10) {
    case 1:
      return `${place}st`;
    case 2:
      return `${place}nd`;
    case 3:
      return `${place}rd`;
    default:
      return `${place}th`;
  }
}

/** "3rd", or "2nd–4th" for a run of places. */
export const placeRange = (from: number, count: number): string =>
  count === 1 ? ordinal(from) : `${ordinal(from)}–${ordinal(from + count - 1)}`;

export function prizeSplitToWeights(draft: PrizeSplitDraft): Array<{ quantity: number; reward: string }> {
  switch (draft.mode) {
    case "winner":
      return [{ quantity: 1, reward: "1" }];
    case "equal":
      return [{ quantity: draft.equalPlaces, reward: "1" }];
    case "custom":
      return draft.customRows.map((row) => ({ quantity: row.places, reward: row.percentEach }));
  }
}

/**
 * Reads stored weights (or legacy absolute amounts, which are weights too) back as the editor's model.
 * Places weighted 0 pay nothing and are dropped. Shares round to cents of a percent; the rounding gap
 * goes to one 1st-place row, so the rows total 100.
 */
export function prizeSplitFromWeights(weights: readonly PrizeSplitWeight[] | null | undefined): PrizeSplitDraft {
  const sorted = [...(weights ?? [])]
    .map((slot) => ({ quantity: slot.quantity, weight: new Decimal(slot.reward) }))
    .filter((slot) => slot.weight.gt(0))
    .sort((a, b) => b.weight.cmp(a.weight));
  // Equal weights in separate rows are one band: read apart, 3 × 33.33% would not make 100%.
  const bands: Array<{ quantity: number; weight: Decimal }> = [];
  for (const slot of sorted) {
    const last = bands.at(-1);
    if (last != null && last.weight.eq(slot.weight)) last.quantity += slot.quantity;
    else bands.push({ ...slot });
  }
  const [top] = bands;
  if (top == null) return defaultPrizeSplitDraft();
  if (bands.length === 1)
    return top.quantity === 1
      ? defaultPrizeSplitDraft()
      : { ...defaultPrizeSplitDraft("equal"), equalPlaces: top.quantity };

  const total = bands.reduce((sum, band) => sum.add(band.weight.mul(band.quantity)), new Decimal(0));
  const shares = bands.map((band) => ({
    places: band.quantity,
    share: band.weight.mul(FULL_POT_PERCENT).div(total).toDecimalPlaces(SHARE_DECIMALS),
  }));
  const gap = FULL_POT_PERCENT.sub(shares.reduce((sum, row) => sum.add(row.share.mul(row.places)), new Decimal(0)));
  const [first, ...rest] = shares;
  const adjusted =
    first == null || gap.isZero()
      ? shares
      : first.places === 1
        ? [{ places: 1, share: first.share.add(gap) }, ...rest]
        : gap.gt(0)
          ? [{ places: 1, share: first.share.add(gap) }, { places: first.places - 1, share: first.share }, ...rest]
          : [{ places: first.places - 1, share: first.share }, { places: 1, share: first.share.add(gap) }, ...rest];
  return {
    ...defaultPrizeSplitDraft("custom"),
    customRows: adjusted.map((row) => ({ places: row.places, percentEach: row.share.toString() })),
  };
}

/** Σ(places × share each) of a custom split; must be exactly 100. Unparseable shares count as 0. */
export const customSplitTotalPercent = (rows: readonly PrizeSplitRow[]): Decimal =>
  rows.reduce((sum, row) => sum.add((parseDecimal(row.percentEach) ?? new Decimal(0)).mul(row.places)), new Decimal(0));

export function customSplitRowError(rows: readonly PrizeSplitRow[]): string | null {
  if (rows.some((row) => !isPlaceCount(row.places))) return PRIZE_SPLIT_PLACE_COUNT_MESSAGE;
  if (rows.some((row) => !(parseDecimal(row.percentEach)?.gt(0) ?? false))) return "Every row needs a share above 0%.";
  const outOfOrder = rows.some((row, index) => {
    const above = rows[index - 1];
    const share = parseDecimal(row.percentEach);
    const aboveShare = above == null ? null : parseDecimal(above.percentEach);
    return share != null && aboveShare != null && share.gt(aboveShare);
  });
  return outOfOrder ? "A lower place can't take more than the place above it." : null;
}

export function isPrizeSplitValid(draft: PrizeSplitDraft): boolean {
  switch (draft.mode) {
    case "winner":
      return true;
    case "equal":
      return isPlaceCount(draft.equalPlaces);
    case "custom":
      return (
        customSplitRowError(draft.customRows) == null && customSplitTotalPercent(draft.customRows).eq(FULL_POT_PERCENT)
      );
  }
}

/** Names a draft without parsing it, so a half-typed share never throws. */
export function describePrizeSplitDraft(draft: PrizeSplitDraft): string {
  switch (draft.mode) {
    case "winner":
      return "Winner takes all";
    case "equal":
      return draft.equalPlaces === 1 ? "Winner takes all" : `Top ${draft.equalPlaces} split equally`;
    case "custom":
      return `Top ${paidPlaceCount(draft.customRows.map((row) => ({ quantity: row.places })))} split`;
  }
}

export const describePrizeSplit = (weights: readonly PrizeSplitWeight[] | null | undefined): string =>
  describePrizeSplitDraft(prizeSplitFromWeights(weights));

/** Compares the active mode's split only: a value stashed for another mode is not part of it. */
export function isSamePrizeSplit(a: PrizeSplitDraft, b: PrizeSplitDraft): boolean {
  const left = prizeSplitToWeights(a);
  const right = prizeSplitToWeights(b);
  return (
    left.length === right.length &&
    left.every((slot, index) => {
      const other = right[index];
      const weight = parseDecimal(slot.reward);
      const otherWeight = other == null ? null : parseDecimal(other.reward);
      return (
        other != null &&
        slot.quantity === other.quantity &&
        weight != null &&
        otherWeight != null &&
        weight.eq(otherWeight)
      );
    })
  );
}

export const equalSharePercent = (places: number): string =>
  FULL_POT_PERCENT.div(places).toDecimalPlaces(SHARE_DECIMALS).toString();

/** Σ shares − 100: positive is over the pot, negative is left to assign. */
export const customSplitRemainder = (rows: readonly PrizeSplitRow[]): Decimal =>
  customSplitTotalPercent(rows).sub(FULL_POT_PERCENT);

/** Shares are typed to cents of a percent; extra decimals are cut as typed. */
export function limitShareDecimals(text: string): string {
  const [whole, fraction] = text.split(".");
  return fraction == null ? text : `${whole}.${fraction.slice(0, SHARE_DECIMALS)}`;
}
